import { useEffect } from 'react'

/**
 * Keyboard shortcut cheat-sheet. Triggered by `?` (Shift+/) when the
 * terminal input is empty, dismissed by Esc or clicking the backdrop.
 *
 * Groups shortcuts by category so users can scan quickly instead of
 * hunting through help text. Passive — doesn't execute anything.
 */

const SECTIONS: { title: string; rows: [string, string][] }[] = [
  {
    title: 'Navigation',
    rows: [
      ['Ctrl+K',        'Fuzzy command palette'],
      ['Ctrl+Shift+M',  'Multi-pane split (2×2)'],
      ['Ctrl+R',        'Reverse history search (bash-style)'],
      ['Ctrl+L',        'Clear terminal'],
      ['?',             'This shortcut overlay'],
      ['Esc',           'Close modal / palette / overlay'],
    ],
  },
  {
    title: 'Input',
    rows: [
      ['Tab',           'Autocomplete selected suggestion'],
      ['↑ / ↓',         'Scroll command history (or suggestions)'],
      ['Enter',         'Execute command'],
    ],
  },
  {
    title: 'F-Keys (rebindable — Ctrl+F#)',
    rows: [
      ['F1',  '/help'],
      ['F2',  '/portfolio'],
      ['F3',  '/blotter'],
      ['F4',  '/regime'],
      ['F5',  '/indices'],
      ['F6',  '/signals'],
      ['F7',  '/whales'],
      ['F8',  '/alerts'],
      ['F9',  '/news'],
      ['F10', '/summary'],
      ['F11', '/lookthrough'],
      ['F12', '/tearsheet'],
    ],
  },
  {
    title: 'New: Health & Pricing',
    rows: [
      ['/dashboard',  'Single-call portfolio health snapshot'],
      ['/coverage',   'Holdings priceability + freshness'],
      ['/concentration', 'Top holdings as % of tracked NAV'],
      ['/fx-exposure',   'Currency breakdown bars'],
      ['/movers-today',  'Top GBP movers today'],
      ['/timeline',      'Last 10 reprice runs with NAV deltas'],
      ['/pricing-health','Probe MSN / CoinGecko / FT / FMP / Redis'],
      ['/reprice',       'Force mark-to-market all holdings'],
      ['/whatsnew',      'Recent backend commits, classified'],
    ],
  },
  {
    title: 'Tips',
    rows: [
      ['Type \'/\'',  'Open autocomplete'],
      ['Click ticker in output', 'Open quick menu'],
      ['Click symbol in ticker strip', '/set it as context'],
      ['Snapshot ↗',  'Copy last 200 lines as markdown'],
      ['/quote ISIN', 'Routes through FT pricer for UK funds'],
    ],
  },
]

export function ShortcutOverlay({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-16 px-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[80vh] overflow-y-auto bg-black border border-crt-green/40 rounded-lg shadow-2xl shadow-black p-6 font-mono crt-scrollbar"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-crt-bright text-lg font-bold">Keyboard Shortcuts</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-crt-dim hover:text-crt-green text-sm"
            aria-label="Close"
          >
            [Esc]
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTIONS.map(section => (
            <section key={section.title}>
              <h3 className="text-crt-amber text-sm uppercase tracking-wider mb-2">
                {section.title}
              </h3>
              <dl className="space-y-1 text-sm">
                {section.rows.map(([key, desc]) => (
                  <div key={key} className="flex items-baseline gap-3">
                    <dt className="shrink-0 min-w-[96px]">
                      <kbd className="px-1.5 py-0.5 border border-crt-dim/50 rounded text-xs text-crt-cyan">
                        {key}
                      </kbd>
                    </dt>
                    <dd className="text-crt-green/80 flex-1">{desc}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <p className="text-crt-dim text-xs mt-6 pt-4 border-t border-crt-green/10">
          Tip: any command also works via the fuzzy palette (Ctrl+K) — no need to memorise F-keys.
        </p>
      </div>
    </div>
  )
}
